import React, { useEffect, useState, useCallback } from "react";
import { Modal, ModalHeader, ModalBody, Spinner, Alert, Input, Button, Label, Badge } from "reactstrap";
import { getUserStatus, requestFaucetCode } from "../../helpers/fakebackend_helper";

const HeaderCreditWidget = () => {
    const [credit, setCredit] = useState(null);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);

    const refreshCredit = useCallback(async () => {
        try {
            const res = await getUserStatus();
            if (res && res.data) {
                setCredit((res.data.sub_credit_remain || 0) + (res.data.bought_credit || 0));
            }
        } catch (e) { /* ignore */ }
    }, []);

    useEffect(() => {
        refreshCredit();
        const t = setInterval(refreshCredit, 60_000);
        const onChanged = () => refreshCredit();
        window.addEventListener("credit-changed", onChanged);
        return () => {
            clearInterval(t);
            window.removeEventListener("credit-changed", onChanged);
        };
    }, [refreshCredit]);

    const openFaucet = async () => {
        setOpen(true);
        setError("");
        setCopied(false);
        if (data) return;
        setLoading(true);
        try {
            const res = await requestFaucetCode();
            if (res && res.success) setData(res.data);
            else setError((res && res.message) || "Could not generate faucet code.");
        } catch (e) {
            setError(typeof e === "string" ? e : (e && e.message) || "Network error.");
        } finally {
            setLoading(false);
        }
    };

    const closeFaucet = () => {
        setOpen(false);
        // Refresh credit shortly after closing in case user already claimed in Telegram
        setTimeout(refreshCredit, 1500);
    };

    const copyCode = () => {
        if (!data || !data.code) return;
        navigator.clipboard.writeText(`/faucet ${data.code}`).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {});
    };

    return (
        <React.Fragment>
            <div className="header-item d-flex align-items-center gap-2 ms-2">
                <a
                    href="/plan"
                    className="ah-credit-chip"
                    title="Your credit balance — click to buy more"
                    style={{ textDecoration: "none" }}
                >
                    <i className="ri-coin-line"></i>
                    <span className="ah-credit-num">{credit === null ? "—" : credit.toLocaleString("en-US")}</span>
                    <span className="ah-credit-unit d-none d-md-inline">credits</span>
                </a>
                <button
                    type="button"
                    className="btn btn-sm ah-credit-claim"
                    onClick={openFaucet}
                    title="Claim free credits"
                >
                    <i className="ri-gift-2-fill me-1"></i>
                    <span className="d-none d-md-inline">Claim</span>
                </button>
            </div>

            <Modal isOpen={open} toggle={closeFaucet} centered size="md">
                <ModalHeader toggle={closeFaucet}>
                    <i className="ri-gift-2-fill text-warning me-2"></i> Free Faucet Credits
                </ModalHeader>
                <ModalBody className="p-4">
                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner color="warning" />
                            <p className="text-muted mt-3 mb-0">Generating your faucet code...</p>
                        </div>
                    ) : error ? (
                        <Alert color="danger" className="mb-0">{error}</Alert>
                    ) : data ? (
                        <div>
                            <p className="fs-15 mb-3">
                                Send the command below in our Telegram bot to receive
                                {" "}<strong>{data.credit_amount} free credits</strong>.
                            </p>
                            <Label className="fw-medium mb-1">Your faucet command</Label>
                            <div className="d-flex gap-2 mb-3">
                                <Input readOnly value={`/faucet ${data.code}`} style={{ fontFamily: "monospace", fontWeight: 600 }} />
                                <Button color={copied ? "success" : "primary"} onClick={copyCode}>
                                    <i className={copied ? "ri-check-line me-1" : "ri-file-copy-line me-1"}></i>
                                    {copied ? "Copied" : "Copy"}
                                </Button>
                            </div>
                            <ol className="text-muted fs-13 ps-3 mb-3">
                                <li>Click <strong>Open Telegram</strong> below.</li>
                                <li>Paste the command above and send it to the bot.</li>
                                <li>Bot will reply and add credits to your account instantly.</li>
                            </ol>
                            <Alert color="warning" className="fs-12 mb-3 py-2">
                                <i className="ri-time-line me-1"></i>
                                Code expires in {Math.max(1, Math.round((data.ttl_seconds || 900) / 60))} minutes. One claim per Telegram account.
                            </Alert>
                            <a
                                href={data.group_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary w-100"
                            >
                                <i className="ri-telegram-fill me-1"></i> Open Telegram
                            </a>
                        </div>
                    ) : null}
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
};

export default HeaderCreditWidget;
